
import React from 'react';
import { useTheme } from "@/contexts/ThemeContext";

interface AvgViewBarChartProps {
  value: number | string;
  maxValue: number;
  className?: string;
  shouldAnimate?: boolean;
}

export const AvgViewBarChart = ({ 
  value, 
  maxValue, 
  className = "",
  shouldAnimate = false 
}: AvgViewBarChartProps) => {
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  
  // Convert value to number if it's a string
  const numericValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
  
  // Calculate percentage width (minimum 5% for visibility when value > 0)
  const percentage = maxValue > 0 ? Math.max((numericValue / maxValue) * 100, numericValue > 0 ? 5 : 0) : 0;
  
  // Generate unique animation delay for staggered effect
  const animationDelay = shouldAnimate ? Math.random() * 300 : 0;
  
  // Determine color based on avg view count
  const getBarColor = (count: number) => {
    if (count >= 10000) {
      return isLightMode ? 'bg-purple-500' : 'bg-purple-600'; // Purple for 10k+
    } else if (count >= 3000) {
      return isLightMode ? 'bg-purple-300' : 'bg-purple-400'; // Subtle purple for 3k-10k
    } else if (count > 0) {
      return isLightMode ? 'bg-purple-100' : 'bg-purple-200'; // Light purple for 0-3k
    }
    return isLightMode ? 'bg-gray-300' : 'bg-gray-600'; // Gray for 0
  };
  
  const barColor = getBarColor(numericValue);
  
  return (
    <div className={`relative flex items-center w-full max-w-full overflow-hidden ${className}`}>
      {/* Background bar */}
      <div className={`absolute inset-0 rounded overflow-hidden ${isLightMode ? 'bg-gray-100' : 'bg-gray-800'}`} />
      
      {/* Progress bar with color categorization and subtle stripes */}
      <div 
        className={`absolute left-0 top-0 h-full rounded overflow-hidden transition-all duration-1000 ease-out ${barColor}`}
        style={{ 
          width: shouldAnimate ? `${Math.min(percentage, 100)}%` : `${Math.min(percentage, 100)}%`,
          maxWidth: '100%',
          backgroundImage: isLightMode 
            ? `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 8px,
                rgba(255, 255, 255, 0.15) 8px,
                rgba(255, 255, 255, 0.15) 16px
              )`
            : `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 8px,
                rgba(255, 255, 255, 0.1) 8px,
                rgba(255, 255, 255, 0.1) 16px
              )`,
          animation: shouldAnimate 
            ? `growWidthAvgView 1000ms ease-out ${animationDelay}ms both, moveStripesAvgView 3s linear infinite`
            : 'none'
        }}
      />
      
      {/* Value text with proper overflow handling */}
      <div className={`relative z-10 px-2 py-1 font-medium text-sm truncate w-full ${
        isLightMode ? 'text-gray-900' : 'text-gray-100'
      }`}>
        {typeof value === "string" ? value : value.toLocaleString()}
      </div>
      
      {/* Global CSS for animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes growWidthAvgView {
            from {
              width: 0%;
            }
            to {
              width: ${Math.min(percentage, 100)}%;
            }
          }
          
          @keyframes moveStripesAvgView {
            0% {
              background-position: 0 0;
            }
            100% {
              background-position: 32px 0;
            }
          }
        `
      }} />
    </div>
  );
};
